import { buildQryString } from 'util/ts-utils'
import { hostsConfig, turnToConfig } from 'init/config'

export const ORIGIN_URL = `${hostsConfig.protocol}://${hostsConfig.originHost}/v5`
export const CDN_URL = `${hostsConfig.protocol}://${hostsConfig.cdnHost}/v5`

/**
 *  options:
 *    body - body to be sent in the request
 *    headers - to include a header, set as an object with the keys being the header tag and the
 *              value being the header value. Example,
 *              headers = {'Content-Type': 'application/json'}
 *    params - query parameters
 *    withCredentials - when true, it will send cookies along.  When false, it will not.
 *                      Default is true.
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'
export interface IRequestOptions {
  body?: any
  formData?: FormData // for file upload, but could be used for other form submissions
  headers?: { [key: string]: string }
  params?: { [key: string]: string }
  progressCallback?: any // a function for file upload to report progress
  withCredentials?: boolean
}

function createRequest<T = any>(
  method: HttpMethod,
  url: string,
  options: IRequestOptions,
  resolve: (xhr: TTXMLHttpRequest<T>) => void,
  reject: (xhr: TTXMLHttpRequest<T>) => void
): TTXMLHttpRequest<T> {
  const xhr: TTXMLHttpRequest<T> = new XMLHttpRequest()
  xhr.addEventListener('load', () => {
    if (xhr.responseText) {
      // assume json
      try {
        xhr.data = JSON.parse(xhr.responseText)
      } catch (e) {
        xhr.data = {} as any
        reject(xhr)
        return
      }
    }
    resolve(xhr)
  })
  const onError = () => {
    if (xhr.responseText) {
      // assume json
      try {
        xhr.data = JSON.parse(xhr.responseText)
      } catch (e) {
        xhr.data = {} as any
      }
    }
    reject(xhr)
  }
  xhr.addEventListener('error', onError)
  xhr.addEventListener('abort', onError)

  const params = options.params || {}
  // open needs to be called before setting content-type header for IE10-11
  xhr.open(method, `${url}?${buildQryString(params)}`)

  const headers = options.headers
  if (headers) {
    Object.keys(headers).forEach(h => xhr.setRequestHeader(h, headers[h]))
  }
  // when true, allow CORS to send cookies
  xhr.withCredentials = options.withCredentials || false
  return xhr
}

export function executeRequest<T = any>(
  method: HttpMethod,
  uri: string,
  options: IRequestOptions = {}
): Promise<TTXMLHttpRequest<T>> {
  return new Promise((resolve, reject) => {
    options.params = options.params || {}
    // set locale on all calls that doesn't already have that value in the path.
    if (!options.params.locale && uri.indexOf(`${turnToConfig.locale}`) === -1) {
      options.params.locale = turnToConfig.locale
    }

    const url = `${ORIGIN_URL}${uri}`
    if (!options.headers) {
      options.headers = {}
    }
    // all but static (CDN) calls need to have this header for CORS allow origin
    options.headers['X-Turnto-Sitekey'] = hostsConfig.siteKey

    if (options.body && !options.headers['Content-Type']) {
      options.headers['Content-Type'] = 'application/json'
    } else if (options.formData && options.headers['Content-Type']) {
      // making sure to let xhr/formData set Content-Type. Needed for multi-part boundary
      delete options.headers['Content-Type']
    }

    const xhr = createRequest<T>(method, url, options, resolve, reject)

    // if a callback is provided for file upload progress
    if (options.progressCallback && xhr.upload) {
      xhr.upload.addEventListener('progress', options.progressCallback, false)
    }

    if (options.body) {
      xhr.send(JSON.stringify(options.body))
    } else if (options.formData) {
      xhr.send(options.formData)
    } else {
      xhr.send()
    }
  })
}

export function executeStaticRequest<T = any>(
  uri: string,
  options: IRequestOptions = {}
): Promise<TTXMLHttpRequest<T>> {
  return new Promise((resolve, reject) => {
    const url = `${CDN_URL}${uri}`
    const xhr = createRequest<T>('GET', url, options, resolve, reject)
    xhr.send()
  })
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/services/http-client.ts